import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  useTheme,
  alpha,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Skeleton
} from '@mui/material';
import { db, auth } from '../firebase';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import BottomNav from './BottomNav';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { format, startOfMonth, endOfMonth, subMonths, isWithinInterval } from 'date-fns';

// Define interfaces
interface Commission {
  id?: string;
  staffId: string;
  staffName: string;
  saleId: string;
  saleNumber: string;
  serviceId: string;
  serviceName: string;
  servicePrice: number;
  commissionRate: number;
  amount: number;
  date: any; // Firestore Timestamp
  status: 'pending' | 'paid' | 'voided';
  customerName?: string;
}

interface ServicePerformance {
  serviceName: string;
  count: number;
  totalRevenue: number;
  totalCommission: number;
  averageCommission: number;
}

interface MonthlyData {
  month: string;
  totalCommission: number;
  totalSales: number;
  serviceCount: number;
}

const PerformanceAnalyticsView: React.FC = () => {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [servicePerformance, setServicePerformance] = useState<ServicePerformance[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [topService, setTopService] = useState<ServicePerformance | null>(null);

  // Summary metrics
  const [totalCommission, setTotalCommission] = useState(0);
  const [totalServices, setTotalServices] = useState(0);
  const [avgCommissionPerService, setAvgCommissionPerService] = useState(0);
  const [currentMonthCommission, setCurrentMonthCommission] = useState(0);
  const [previousMonthCommission, setPreviousMonthCommission] = useState(0);
  const [monthlyGrowth, setMonthlyGrowth] = useState(0);

  // Fetch user data and commissions
  useEffect(() => {
    const fetchUserData = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          setUserData({
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || 'Staff Member',
          });

          await fetchCommissionData(currentUser.email || '');
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, []);

  const fetchCommissionData = async (userEmail: string) => {
    try {
      // First get the staff ID based on email
      const staffRef = collection(db, 'staff');
      const staffQuery = query(
        staffRef,
        where('email', '==', userEmail)
      );

      const staffSnapshot = await getDocs(staffQuery);
      if (staffSnapshot.empty) {
        console.log('No staff record found for this user');
        return;
      }

      const staffId = staffSnapshot.docs[0].id;

      // Now fetch commissions for this staff
      const commissionsRef = collection(db, 'commissions');
      const commissionsQuery = query(
        commissionsRef,
        where('staffId', '==', staffId),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(commissionsQuery);
      const commissionsData: Commission[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as Omit<Commission, 'id'>
      }));

      setCommissions(commissionsData);
      processCommissionData(commissionsData);
    } catch (error) {
      console.error('Error fetching commission data:', error);
    }
  };

  const processCommissionData = (data: Commission[]) => {
    if (!data.length) return;

    // Calculate overall metrics
    let total = 0;
    const serviceMap = new Map<string, ServicePerformance>();
    const monthlyMap = new Map<string, MonthlyData>();

    // Initialize last 6 months data
    for (let i = 5; i >= 0; i--) {
      const monthDate = subMonths(new Date(), i);
      const monthKey = format(monthDate, 'yyyy-MM');
      const monthLabel = format(monthDate, 'MMM yyyy');

      monthlyMap.set(monthKey, {
        month: monthLabel,
        totalCommission: 0,
        totalSales: 0,
        serviceCount: 0
      });
    }

    // Current month range
    const now = new Date();
    const currentMonthStart = startOfMonth(now);
    const currentMonthEnd = endOfMonth(now);

    // Previous month range
    const prevMonthStart = startOfMonth(subMonths(now, 1));
    const prevMonthEnd = endOfMonth(subMonths(now, 1));

    let currentMonthTotal = 0;
    let prevMonthTotal = 0;

    // Process each commission
    data.forEach(commission => {
      if (commission.status === 'voided') return;

      // Add to total
      total += commission.amount;

      // Process by service
      const serviceName = commission.serviceName;
      if (!serviceMap.has(serviceName)) {
        serviceMap.set(serviceName, {
          serviceName,
          count: 0,
          totalRevenue: 0,
          totalCommission: 0,
          averageCommission: 0
        });
      }

      const serviceData = serviceMap.get(serviceName)!;
      serviceData.count += 1;
      serviceData.totalRevenue += commission.servicePrice;
      serviceData.totalCommission += commission.amount;
      serviceData.averageCommission = serviceData.totalCommission / serviceData.count;

      // Process by month
      const commissionDate = commission.date.toDate();
      const monthKey = format(commissionDate, 'yyyy-MM');

      if (monthlyMap.has(monthKey)) {
        const monthData = monthlyMap.get(monthKey)!;
        monthData.totalCommission += commission.amount;
        monthData.totalSales += commission.servicePrice;
        monthData.serviceCount += 1;
      }

      // Check if in current month
      if (isWithinInterval(commissionDate, { start: currentMonthStart, end: currentMonthEnd })) {
        currentMonthTotal += commission.amount;
      }

      // Check if in previous month
      if (isWithinInterval(commissionDate, { start: prevMonthStart, end: prevMonthEnd })) {
        prevMonthTotal += commission.amount;
      }
    });

    // Set all the calculated values
    setTotalCommission(total);
    setTotalServices(data.length);
    setAvgCommissionPerService(data.length ? total / data.length : 0);
    setCurrentMonthCommission(currentMonthTotal);
    setPreviousMonthCommission(prevMonthTotal);

    // Calculate growth
    const growth = prevMonthTotal ? ((currentMonthTotal - prevMonthTotal) / prevMonthTotal) * 100 : 0;
    setMonthlyGrowth(growth);

    // Convert maps to arrays for rendering
    const serviceArray = Array.from(serviceMap.values());
    serviceArray.sort((a, b) => b.totalCommission - a.totalCommission);
    setServicePerformance(serviceArray);

    if (serviceArray.length > 0) {
      setTopService(serviceArray[0]);
    }

    // Monthly data for chart
    const monthlyArray = Array.from(monthlyMap.values());
    setMonthlyData(monthlyArray);
  };

  // Format currency with MYR
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Box sx={{
      flexGrow: 1,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#f8f9fa',
      pb: 8
    }}>
      {/* Modern Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '0 0 32px 32px',
          pt: 3,
          pb: 4,
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                Performance
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Your analytics & insights
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          pt: 3,
          flexGrow: 1,
          overflowY: 'auto'
        }}
      >
        {loading ? (
          <Box>
            {/* Skeleton loaders */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Card sx={{ borderRadius: '16px' }}>
                  <CardContent>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="80%" />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ borderRadius: '16px' }}>
                  <CardContent>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="80%" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            {[1, 2, 3].map((i) => (
              <Card key={i} sx={{ mb: 2, borderRadius: '16px' }}>
                <CardContent>
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="rectangular" height={60} sx={{ mt: 1, borderRadius: '8px' }} />
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Card
                  sx={{
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    height: '100%',
                    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.25)',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AttachMoneyIcon sx={{ fontSize: 28, opacity: 0.9 }} />
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Total Earnings
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatCurrency(totalCommission)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card
                  sx={{
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    height: '100%',
                    boxShadow: '0 8px 16px rgba(240, 147, 251, 0.25)',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimelineIcon sx={{ fontSize: 28, opacity: 0.9 }} />
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Services Done
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {totalServices}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Average Commission Card */}
            <Card
              sx={{
                mb: 3,
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
                boxShadow: '0 8px 16px rgba(67, 233, 123, 0.25)',
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                  Average per Service
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {formatCurrency(avgCommissionPerService)}
                </Typography>
              </CardContent>
            </Card>

            {/* Monthly Performance Card */}
            <Card
              sx={{
                mb: 3,
                borderRadius: '20px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DateRangeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Monthly Performance
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Month
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(currentMonthCommission)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Previous Month
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(previousMonthCommission)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Growth Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {monthlyGrowth > 0 ? (
                      <TrendingUpIcon sx={{ color: 'success.main', mr: 0.5, fontSize: 20 }} />
                    ) : (
                      <TrendingDownIcon sx={{ color: 'error.main', mr: 0.5, fontSize: 20 }} />
                    )}
                    <Chip
                      label={`${monthlyGrowth.toFixed(1)}%`}
                      size="small"
                      sx={{
                        background: monthlyGrowth >= 0
                          ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
                          : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* 6-Month Trend */}
            <Card
              sx={{
                mb: 3,
                borderRadius: '20px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  6-Month Commission Trend
                </Typography>

                {monthlyData.map((month, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {month.month}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(month.totalCommission)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (month.totalCommission / (Math.max(...monthlyData.map(m => m.totalCommission)) || 1)) * 100)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: 4,
                        }
                      }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>

            {/* Top Performing Services */}
            <Card
              sx={{
                mb: 3,
                borderRadius: '20px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Top Performing Services
                </Typography>

                {servicePerformance.length > 0 ? (
                  <List disablePadding>
                    {servicePerformance.slice(0, 5).map((service, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <Divider component="li" sx={{ my: 1 }} />}
                        <ListItem
                          sx={{
                            px: 0,
                            py: 1.5,
                            bgcolor: index === 0 ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
                            borderRadius: '12px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            }
                          }}
                        >
                          {index === 0 && (
                            <StarIcon
                              sx={{
                                fontSize: 20,
                                color: 'warning.main',
                                mr: 1
                              }}
                            />
                          )}
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight={index === 0 ? 600 : 500}>
                                {service.serviceName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {service.count} services • Avg: {formatCurrency(service.averageCommission)}
                              </Typography>
                            }
                          />
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={index === 0 ? 'primary.main' : 'text.primary'}
                          >
                            {formatCurrency(service.totalCommission)}
                          </Typography>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    No service data available
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Best Performing Stats - Highlighted Card */}
            {topService && (
              <Card
                sx={{
                  mb: 3,
                  borderRadius: '24px',
                  background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                  color: 'white',
                  boxShadow: '0 12px 24px rgba(25, 118, 210, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 1.4l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM38.59 40l-2.83-2.83 1.41-1.41L40 38.59V40h-1.41zM40 1.41l-2.83 2.83-1.41-1.41L38.59 0H40v1.41z\'/%3E%3C/g%3E%3C/svg%3E")',
                    opacity: 0.2,
                  }
                }}
              >
                <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StarIcon sx={{ fontSize: 24, mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                      HIGHEST EARNING SERVICE
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                    {topService.serviceName}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Total Earnings
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(topService.totalCommission)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        Services Done
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {topService.count}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default PerformanceAnalyticsView;
